import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

// Justificativa dos atrasos do BPO RH — mesma mecânica do modal do contábil:
// ao entrar no módulo, o operador vê o que está atrasado na carteira dele
// (admissão, rescisão, férias no limite legal e experiência vencida sem aviso)
// e diz, caso a caso, se a causa é INTERNA (nossa) ou EXTERNA (cliente /
// terceiro), com uma observação. A régua do "atrasado" é a do Painel do Operador.
// Revalidação (29/09/2026): caso que CONTINUA atrasado 2 dias depois da última
// observação volta aqui com a observação anterior à vista, e a operadora diz se
// avançou (pode estar em outra etapa) ou se continua na mesma, com texto novo.
const API = process.env.REACT_APP_API_BASE;
const TAMANHO_LOTE = 10;

const ICONE = { admissao: 'fa-user-plus', rescisao: 'fa-user-minus', ferias: 'fa-umbrella-beach', ferias_aprovacao: 'fa-user-check',
  ferias_solicitar: 'fa-calendar-plus', experiencia: 'fa-hourglass-end', contrato_prorrogacao: 'fa-file-signature',
  folha: 'fa-file-invoice-dollar', beneficio: 'fa-utensils', ferias_afastamento: 'fa-user-injured', fechamento_cliente: 'fa-envelope-open-text',
  financeiro: 'fa-money-check-dollar', exame: 'fa-notes-medical', plano_saude: 'fa-heart-pulse' };
const COR = { admissao: '#0a6ea1', rescisao: '#a11d2b', ferias: '#b07d00', ferias_aprovacao: '#b07d00', ferias_solicitar: '#b07d00',
  experiencia: '#6f42c1', contrato_prorrogacao: '#6f42c1', folha: '#00205b', beneficio: '#0e6e86', ferias_afastamento: '#5b21b6', fechamento_cliente: '#00205b',
  financeiro: '#a11d2b', exame: '#0e6e86', plano_saude: '#0e6e86' };

const chaveDe = (i) => `${i.tipo}|${i.cliente_id}|${i.convenia_id}|${i.referencia}`;
// ordem das seções do modal — uma por atividade
const ORDEM_TIPOS = ['financeiro', 'folha', 'fechamento_cliente', 'beneficio', 'admissao', 'rescisao', 'ferias', 'ferias_aprovacao', 'ferias_solicitar', 'ferias_afastamento', 'experiencia', 'contrato_prorrogacao', 'exame', 'plano_saude'];
const TITULO = { admissao: 'Admissões', rescisao: 'Rescisões', ferias: 'Férias — limite legal estourado',
  ferias_aprovacao: 'Férias — aprovações não autorizadas (pagamento em até 30 dias)',
  ferias_solicitar: 'Férias para solicitar (prazo em até 5 dias)',
  experiencia: 'Experiência a vencer sem e-mail', contrato_prorrogacao: 'Termo de prorrogação sem PDF',
  folha: 'Fechamento de folha fora do prazo', beneficio: 'VA · Home Office · VT fora do prazo (dia 25)',
  ferias_afastamento: 'Afastamento / licença — confirmar se continua (a cada 30 dias)',
  fechamento_cliente: 'Fechamento do mês não enviado ao cliente (até o dia 5)',
  financeiro: 'Financeiro — pagamento vencido sem comunicar o cliente', exame: 'Exames periódicos vencidos',
  plano_saude: 'Plano de saúde — 3 meses sem a adesão registrada' };

const ModalAtrasosRh = () => {
  const [aberto, setAberto] = useState(false);
  const abertoRef = useRef(false);            // o timer lê o estado atual, não o do mount
  useEffect(() => { abertoRef.current = aberto; }, [aberto]);
  const [itens, setItens] = useState([]);
  const [total, setTotal] = useState(0);
  const [revalidacoes, setRevalidacoes] = useState(0);
  const [respostas, setRespostas] = useState({});   // chave -> {origem, observacao}
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [chkAberto, setChkAberto] = useState({});   // chave -> true (checklist expandido)

  // Verifica ao entrar no módulo e de novo a cada 15 min enquanto a operadora
  // fica na área: um caso que vira "vence em 2 dias" durante o dia passa a
  // cobrar a observação sem precisar recarregar a página (23/09/2026).
  useEffect(() => {
    let vivo = true;
    const verificar = () => {
      const token = localStorage.getItem('access');
      if (!token) return;
      axios.get(`${API}/api/bporh/atrasos/`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => {
          if (!vivo) return;
          const pend = r.data.pendentes || [];
          if (r.data.success && pend.length) {
            setItens(pend.slice(0, TAMANHO_LOTE));
            setTotal(pend.length);
            setRevalidacoes(r.data.revalidacoes || 0);
            setRespostas(inicial(pend.slice(0, TAMANHO_LOTE)));
            setAberto(true);
          }
        })
        .catch((e) => console.error('Erro ao verificar atrasos do BPO RH:', e));
    };
    verificar();
    const t = setInterval(() => { if (!abertoRef.current) verificar(); }, 15 * 60 * 1000);
    return () => { vivo = false; clearInterval(t); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const responder = (k, patch) => setRespostas((s) => ({ ...s, [k]: { ...(s[k] || {}), ...patch } }));
  // revalidação já vem com a causa anterior marcada (a operadora troca se mudou);
  // a observação é sempre nova — repetir a antiga é o que o gestor não quer ver
  function inicial(lista) {
    const r = {};
    lista.forEach((i) => { if (i.revalidacao && i.ultima?.origem) r[chaveDe(i)] = { origem: i.ultima.origem }; });
    return r;
  }
  const completo = (k, i) => {
    const r = respostas[k] || {};
    const obs = (r.observacao || '').trim();
    if (!r.origem || obs.length < 5) return false;
    if (i && i.revalidacao) {
      if (!r.andamento) return false;
      if (obs.toLowerCase() === (i.ultima?.observacao || '').trim().toLowerCase()) return false;
    }
    return true;
  };
  const faltam = itens.filter((i) => !completo(chaveDe(i), i)).length;

  const salvar = async () => {
    if (faltam) return;
    setSalvando(true); setErro('');
    try {
      const token = localStorage.getItem('access');
      const res = await axios.post(`${API}/api/bporh/atrasos/`, {
        itens: itens.map((i) => ({
          tipo: i.tipo, cliente_id: i.cliente_id, convenia_id: i.convenia_id, referencia: i.referencia,
          nome: i.nome, cliente: i.cliente, dias_atraso: i.dias_atraso, onde: i.onde,
          origem: respostas[chaveDe(i)].origem, observacao: respostas[chaveDe(i)].observacao.trim(),
          revalidacao: !!i.revalidacao, andamento: respostas[chaveDe(i)].andamento || null,
        })),
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) {
        // sobrou lote? busca de novo; senão fecha
        const r2 = await axios.get(`${API}/api/bporh/atrasos/`, { headers: { Authorization: `Bearer ${token}` } });
        const pend = r2.data.pendentes || [];
        if (pend.length) {
          setItens(pend.slice(0, TAMANHO_LOTE)); setTotal(pend.length);
          setRevalidacoes(r2.data.revalidacoes || 0); setRespostas(inicial(pend.slice(0, TAMANHO_LOTE)));
        }
        else setAberto(false);
      } else {
        setErro(res.data.mensagem || 'Não foi possível salvar.');
      }
    } catch (e) {
      setErro(e.response?.data?.mensagem || 'Erro ao salvar as justificativas.');
    } finally {
      setSalvando(false);
    }
  };

  if (!aberto) return null;

  return (
    <div className="modal" tabIndex="-1"
      style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 9999, backdropFilter: 'blur(3px)' }}>
      <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: 1320 }}>
        <div className="modal-content shadow-lg">
          {/* sem botão de fechar nem "depois": a justificativa é obrigatória, como no contábil */}
          <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
            <div>
              <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                <i className="fas fa-triangle-exclamation me-2 text-warning"></i>
                AÇÃO OBRIGATÓRIA: atrasos na sua carteira — informe a causa
              </h5>
              <small style={{ color: '#6c757d' }}>
                {total} caso(s) atrasado(s) para justificar
                {revalidacoes > 0 ? ` · ${revalidacoes} continua(m) atrasado(s) 2 dias depois da última observação` : ''}
                {total > itens.length ? ` — os ${itens.length} mais atrasados agora, o resto em seguida` : ''}
              </small>
            </div>
          </div>

          <div className="modal-body">
            <div className="alert alert-light border small py-2">
              Para cada caso, marque se o atraso é <b>interno</b> (gargalo nosso) ou <b>externo</b>
              (depende do cliente ou de terceiro) e escreva em uma linha o que está travando.
              Causa externa não isenta: registre o que já foi cobrado do cliente.
              {revalidacoes > 0 && (
                <div className="mt-1">
                  <i className="fas fa-rotate me-1 text-primary"></i>
                  Casos com <b>“Atualizar observação”</b> já tinham justificativa, mas continuam atrasados 2 dias depois:
                  diga se <b>avançou</b> (pode estar em outra etapa) ou se <b>continua na mesma</b>, e escreva o que mudou.
                </div>
              )}
            </div>
            {erro && <div className="alert alert-danger py-2 small"><i className="fas fa-circle-exclamation me-2"></i>{erro}</div>}

            {ORDEM_TIPOS.filter((t) => itens.some((i) => i.tipo === t)).map((t) => {
              const doTipo = itens.filter((i) => i.tipo === t);
              const feitos = doTipo.filter((i) => completo(chaveDe(i), i)).length;
              return (
                <section key={t} className="mb-4">
                  <div className="d-flex align-items-center gap-2 mb-2 pb-1" style={{ borderBottom: `2px solid ${COR[t]}` }}>
                    <i className={`fas ${ICONE[t]}`} style={{ color: COR[t] }}></i>
                    <h6 className="fw-bold mb-0" style={{ color: COR[t] }}>{TITULO[t]}</h6>
                    <span className="badge rounded-pill" style={{ background: COR[t] }}>{doTipo.length}</span>
                    <span className="small text-muted ms-auto">{feitos}/{doTipo.length} justificado(s)</span>
                  </div>
                  {doTipo.map((i) => {
              const k = chaveDe(i);
              const r = respostas[k] || {};
              const ok = completo(k, i);
              const ult = i.revalidacao ? (i.ultima || {}) : null;
              return (
                <div key={k} className="card mb-3" style={{ borderColor: ok ? '#198754' : COR[i.tipo] || '#adb5bd', borderWidth: 2 }}>
                  <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2"
                    style={{ background: ok ? '#f0fdf4' : '#f8f9fa' }}>
                    <span className="fw-bold" style={{ fontSize: '.92rem' }}>
                      <i className={`fas ${ok ? 'fa-circle-check text-success' : ICONE[i.tipo]} me-2`} style={{ color: ok ? undefined : COR[i.tipo] }}></i>
                      {i.nome}
                      <span className="text-muted fw-normal"> · {i.cliente}{i.cargo ? ` · ${i.cargo}` : ''}</span>
                    </span>
                    <span className="d-flex align-items-center gap-1">
                      {(i.checklist || []).length > 0 && (() => {
                        const feitas = i.checklist.filter((t) => t.estado === 'concluido').length;
                        const total = i.checklist.length;
                        return (
                          <button type="button"
                            className={`btn btn-sm py-0 px-2 ${chkAberto[k] ? 'btn-secondary' : (feitas > 0 ? 'btn-warning' : 'btn-outline-secondary')}`}
                            style={{ fontSize: '.75rem' }}
                            onClick={() => setChkAberto((s) => ({ ...s, [k]: !s[k] }))}
                            title={chkAberto[k] ? 'Fechar o checklist' : 'Ver o checklist'}>
                            <i className={`fas ${chkAberto[k] ? 'fa-chevron-up' : 'fa-list-check'} me-1`}></i>{feitas}/{total}
                          </button>
                        );
                      })()}
                      <span className={`badge ${i.dias_atraso >= 0 ? 'bg-danger' : 'bg-warning text-dark'}`}>
                        {i.dias_atraso >= 0 ? `${i.dias_atraso} dia(s) de atraso` : `vence em ${-i.dias_atraso} dia(s)`}
                      </span>
                      {i.link && (
                        <Link to={i.link} className="btn btn-sm btn-link p-0 ms-1" title="Abrir o módulo" target="_blank">
                          <i className="fas fa-arrow-up-right-from-square"></i>
                        </Link>
                      )}
                    </span>
                  </div>
                  <div className="card-body py-2">
                    <div className="row g-2 small mb-2">
                      {ult && (
                        <div className="col-12">
                          <div className="rounded p-2 mb-1" style={{ background: '#eef4ff', border: '1px solid #b9cdf5' }}>
                            <div className="fw-bold mb-1" style={{ color: '#0a3d8f', fontSize: '.8rem' }}>
                              <i className="fas fa-rotate me-1"></i>Atualizar observação · a última foi há {ult.dias_desde} dia(s)
                              {ult.operador ? `, por ${ult.operador}` : ''}{ult.em ? ` (${ult.em})` : ''}
                            </div>
                            <div style={{ fontSize: '.85rem' }}>
                              <span className={`badge me-2 ${ult.origem === 'EXTERNO' ? 'bg-warning text-dark' : 'bg-primary'}`}>{ult.origem === 'EXTERNO' ? 'Externo' : 'Interno'}</span>
                              “{ult.observacao}”
                            </div>
                            {ult.onde && ult.onde !== i.onde && (
                              <div className="mt-1" style={{ fontSize: '.78rem', color: '#0a3d8f' }}>
                                Na última observação estava em: <b>{ult.onde}</b> · agora: <b>{i.onde}</b>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      {(i.detalhes || []).map(([rot, val]) => (
                        <div key={rot} className="col-md-6"><span className="text-muted">{rot}:</span> <b>{val}</b></div>
                      ))}
                      <div className="col-12"><span className="text-muted">Onde está pendente:</span> <b style={{ color: '#a11d2b' }}>{i.onde}</b></div>
                    </div>
                    {(i.checklist || []).length > 0 && chkAberto[k] && (() => {
                      const feitas = i.checklist.filter((t) => t.estado === 'concluido').length;
                      const total = i.checklist.length;
                      const pct = total ? Math.round((feitas / total) * 100) : 0;
                      const ESTADO = {
                        concluido: { icone: 'fa-check', cor: '#198754', rotulo: 'Concluído' },
                        solicitado: { icone: 'fa-paper-plane', cor: '#0dcaf0', rotulo: 'Solicitado' },
                        agendado: { icone: 'fa-calendar-check', cor: '#0d6efd', rotulo: 'Agendado' },
                        pendente: { icone: 'fa-clock', cor: '#ffc107', rotulo: 'Pendente' },
                      };
                      return (
                        <div className="mb-2 border rounded overflow-hidden" style={{ background: '#fff' }}>
                          <div className="d-flex justify-content-between align-items-center px-2 py-1 bg-light small">
                            <span className="fw-semibold"><i className="fas fa-list-check me-2 text-muted"></i>{feitas}/{total} tarefas concluídas</span>
                            <span className={`badge ${feitas === total ? 'bg-success' : 'bg-warning text-dark'}`}>{pct}%</span>
                          </div>
                          <div className="progress" style={{ height: 4, borderRadius: 0 }}>
                            <div className={`progress-bar ${feitas === total ? 'bg-success' : 'bg-warning'}`} style={{ width: `${pct}%` }} />
                          </div>
                          <div className="row g-0">
                            {i.checklist.map((t, idx) => {
                              const e = ESTADO[t.estado] || ESTADO.pendente;
                              return (
                                <div key={idx} className="col-md-6 d-flex align-items-center gap-2 px-2 py-1 border-bottom small"
                                  style={{ opacity: t.estado === 'concluido' ? 0.7 : 1 }}>
                                  <span className="d-inline-flex align-items-center justify-content-center rounded-circle"
                                    style={{ width: 20, height: 20, background: e.cor, color: '#fff', fontSize: '.6rem', flex: 'none' }}>
                                    <i className={`fas ${e.icone}`}></i>
                                  </span>
                                  <span className="flex-grow-1">{t.rotulo}</span>
                                  <span className="text-muted" style={{ fontSize: '.72rem' }}>{e.rotulo}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                    {/* a resposta do operador: bloco próprio, destacado, com rótulos */}
                    <div className="rounded p-2 mt-2"
                      style={{ background: ok ? '#f0fdf4' : '#fff8e1', border: `1px solid ${ok ? '#86efac' : '#f5c542'}` }}>
                      {i.revalidacao && (
                        <div className="mb-2">
                          <label className="form-label fw-bold mb-1" style={{ fontSize: '.8rem' }}>
                            <i className="fas fa-route me-1"></i>Desde a última observação
                            {!r.andamento && <span className="text-danger ms-1">*</span>}
                          </label>
                          <div className="btn-group w-100" role="group">
                            <button type="button" className={`btn ${r.andamento === 'avancou' ? 'btn-success' : 'btn-outline-success'}`}
                              onClick={() => responder(k, { andamento: 'avancou' })}>
                              <i className="fas fa-forward me-1"></i>Avançou (está em outra etapa)
                            </button>
                            <button type="button" className={`btn ${r.andamento === 'mesma' ? 'btn-danger' : 'btn-outline-danger'}`}
                              onClick={() => responder(k, { andamento: 'mesma' })}>
                              <i className="fas fa-pause me-1"></i>Continua na mesma
                            </button>
                          </div>
                        </div>
                      )}
                      <div className="row g-2 align-items-end">
                        <div className="col-md-4">
                          <label className="form-label fw-bold mb-1" style={{ fontSize: '.8rem' }}>
                            <i className="fas fa-diagram-project me-1"></i>Causa do atraso
                            {!r.origem && <span className="text-danger ms-1">*</span>}
                          </label>
                          <div className="btn-group w-100" role="group">
                            <button type="button" className={`btn ${r.origem === 'INTERNO' ? 'btn-primary' : 'btn-outline-primary'}`}
                              onClick={() => responder(k, { origem: 'INTERNO' })} title="Gargalo nosso (Comece)">
                              <i className="fas fa-building me-1"></i>Interno
                            </button>
                            <button type="button" className={`btn ${r.origem === 'EXTERNO' ? 'btn-warning' : 'btn-outline-warning'}`}
                              onClick={() => responder(k, { origem: 'EXTERNO' })} title="Depende do cliente ou de terceiro">
                              <i className="fas fa-people-arrows me-1"></i>Externo
                            </button>
                          </div>
                        </div>
                        <div className="col-md-8">
                          <label className="form-label fw-bold mb-1" style={{ fontSize: '.8rem' }}>
                            <i className="fas fa-pen-to-square me-1"></i>Justificativa
                            {(r.observacao || '').trim().length < 5 && (
                              <span className="text-danger ms-1">
                                {i.revalidacao ? '* o que mudou desde a última (ou por que ainda não andou)' : '* o que está travando e o que já foi feito'}
                              </span>
                            )}
                            {i.revalidacao && (r.observacao || '').trim().length >= 5
                              && (r.observacao || '').trim().toLowerCase() === (ult?.observacao || '').trim().toLowerCase() && (
                              <span className="text-danger ms-1">* escreva uma observação nova, não a mesma de antes</span>
                            )}
                          </label>
                          <textarea className="form-control" rows={2}
                            style={{ fontSize: '.9rem', borderColor: (r.observacao || '').trim().length >= 5 ? '#86efac' : '#f5c542', background: '#fff' }}
                            placeholder={i.revalidacao
                              ? 'Ex.: assinatura chegou, agora aguardando o exame demissional — agendado para 02/10'
                              : 'Ex.: cliente ainda não devolveu a assinatura — cobrado em 15/09, prometeu para 18/09'}
                            value={r.observacao || ''} onChange={(e) => responder(k, { observacao: e.target.value })} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
                  })}
                </section>
              );
            })}
          </div>

          <div className="modal-footer" style={{ borderTop: '1px solid #e9ecef' }}>
            <span className="small text-muted me-auto">
              <i className="fas fa-lock me-1"></i>
              {faltam ? `${faltam} caso(s) ainda sem causa ou sem observação — o módulo libera quando todos estiverem justificados` : 'Tudo preenchido'}
            </span>
            <button type="button" className="btn btn-success btn-sm" onClick={salvar} disabled={salvando || !!faltam}>
              <i className={`fas ${salvando ? 'fa-spinner fa-spin' : 'fa-floppy-disk'} me-2`}></i>
              Salvar {itens.length} justificativa(s)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalAtrasosRh;
